import { ActionButtons } from '@/components/Header/ActionButtons';
import Header from '@/components/Header/Header';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { IconTicketPlus } from '@/icons';
import { TicketListItem } from '@/components/Tickets/TicketListItem';
import { FilterPanel } from '@/components/FilterPanel';
import Input from '@/components/core/Input';
import {
  checkUserPermissions,
  isTenantProfile,
} from '@/components/Auth/checkUserPermissions';
import { FilteredListContainer } from '@/components/FilteredListContainer';
import { VirtualizedList } from '@/components/VirtualizedList.tsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RightPanel } from '@/components/RightPanel/RightPanel';
import {
  FilterType,
  SearchFilterParams,
  SortDirection,
} from '@/types/filterSearch';
import { useAscOrDescIcon } from '@/hooks/useSort';
import { useSearchFilterSort } from '@/hooks/useSearchFilterSort';
import { createSortConfig } from '@/utils/sort';
import Button from '@/components/core/Button';
import {
  useGetTicketsForUserOrganization,
  IssuedTicketFindResultDTO,
  getGetTicketsForUserOrganizationQueryKey,
  useGetCurrentDepartmentTree,
  useGetDepartmentsAccess,
  DepartmentAccessLevelDTO,
  type DepartmentTreeNodeDTO,
} from '@/api/trafikportalen';
import { useVisibleTicketsUpdater } from '@/hooks/useVisibleTicketsUpdater';
import { Loading } from '@/components/Loading';
import { Dropdown, OptionsType } from '@/components/core/Dropdown';
import {
  useTicketStatusOptions,
  TICKET_STATES,
} from '@/hooks/useTicketTranslations';
import { useCanBuyTickets } from '@/hooks/useCanBuyTickets';
import { useTagTitles } from '@/hooks/useTagTitles';
import { DocumentHead } from '@/components/core/DocumentHead';
import {
  isStudentCollectionType,
  COLLECTION_TYPE_LABELS,
  type StudentCollectionType,
} from '@/helpers/collectionTypes';
import { getTravellerClassTags } from '@/helpers/ticketClass';
import { displayedTicketState } from '@/helpers/ticket';

export const Route = createFileRoute('/$tenantId/_layout/tickets/')({
  component: Tickets,
  validateSearch: (search: Record<string, unknown>): SearchFilterParams => {
    return {
      query: search.query as string,
      sort: search.sort as string,
      direction: search.direction as SortDirection,
      productClass: search.productClass as string[],
      travellerClass: search.travellerClass as string[],
      department: search.department as string[],
      status: search.status as string[],
    };
  },
  beforeLoad: ({ params }) => {
    if (
      isTenantProfile() ||
      checkUserPermissions({ permission: 'tenant:viewer' })
    ) {
      throw redirect({ to: '/$tenantId', params });
    }
  },
});

function Tickets() {
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { data: tickets, isLoading } = useGetTicketsForUserOrganization();
  const { data: departmentTree } = useGetCurrentDepartmentTree();
  const { data: departmentsAccess } = useGetDepartmentsAccess();
  const { getStatusTranslation } = useTicketStatusOptions();
  const tagTitles = useTagTitles();
  const canBuyTickets = useCanBuyTickets();
  const rightPanelRef = useRef<HTMLElement>(null);
  const [announcement, setAnnouncement] = useState('');

  const { tenantId } = Route.useParams();
  const {
    query,
    sort,
    direction,
    productClass: filterProductClass,
    travellerClass: filterTravellerClass,
    department: filterDepartment,
    status: filterStatus,
  } = Route.useSearch();

  const sortConfigs = useMemo(() => {
    return createSortConfig<IssuedTicketFindResultDTO>({
      textFields: ['created_at', 'state'],
    });
  }, []);

  const getIcon = useAscOrDescIcon();

  const {
    filterSearch,
    sortedItems,
    setFilterSearch,
    handleSort,
    handleSearch,
    handleSearchParams,
  } = useSearchFilterSort<IssuedTicketFindResultDTO>({
    items: (tickets as []) || [],
    searchKeys: [
      'traveller_first_name',
      'traveller_last_name',
      'id',
      'bobcat_order_id',
      'bobcat_purchase_transaction_id',
      'bobcat_mtb_product_ids',
    ],
    sortConfigs,
    routePath: Route.fullPath,
    defaultSearchParams: {
      sort: 'created_at',
      direction: 'desc',
      productClass: [],
      travellerClass: [],
      department: [],
      status: [],
    },
  });

  const optionsProductClass = useMemo(() => {
    if (!tickets?.length) {
      return [];
    }

    return Array.from(
      new Map(
        tickets.map((ticket) => [
          ticket.product_class,
          {
            id: ticket.product_class,
            label: tagTitles[ticket.product_class] ?? ticket.product_class,
          },
        ]),
      ).values(),
    );
  }, [tickets, tagTitles]);

  const optionsTravellerClass = useMemo(() => {
    if (!tickets?.length) {
      return [];
    }

    const travellerClassTags = new Set<string>();
    tickets.forEach((ticket) => {
      getTravellerClassTags(ticket).forEach((tag) =>
        travellerClassTags.add(tag),
      );
    });

    return Array.from(travellerClassTags)
      .sort((a, b) => a.localeCompare(b))
      .map((tag) => ({ id: tag, label: tagTitles[tag] ?? tag }));
  }, [tickets, tagTitles]);

  const deptTypeMap = useMemo(() => {
    if (!departmentTree) return new Map<string, string>();
    const map = new Map<string, string>();
    const walk = (node: DepartmentTreeNodeDTO) => {
      map.set(node.id, node.type);
      node.children.forEach(walk);
    };
    walk(departmentTree);
    return map;
  }, [departmentTree]);

  const optionsDepartments = useMemo(() => {
    if (!departmentsAccess?.departments) return [];

    return Object.entries(departmentsAccess.departments)
      .filter(([, info]) => info.access_level !== DepartmentAccessLevelDTO.none)
      .map(([id, info]) => {
        const type = deptTypeMap.get(id);
        const label =
          type && isStudentCollectionType(type)
            ? formatMessage(
                COLLECTION_TYPE_LABELS[type as StudentCollectionType],
              )
            : info.department_name;
        return { id, label };
      });
  }, [departmentsAccess, deptTypeMap, formatMessage]);

  const optionsStatus = useMemo(() => {
    return TICKET_STATES.map((state) => ({
      id: state,
      label: getStatusTranslation({ state }),
    }));
  }, [getStatusTranslation]);

  const filteredTickets = useMemo(() => {
    if (!sortedItems) {
      return [];
    }

    if (
      !filterSearch?.productClass ||
      !filterSearch?.travellerClass ||
      !filterSearch?.department ||
      !filterSearch?.status
    ) {
      return sortedItems;
    }

    const isProductClassFiltered = filterSearch.productClass.length > 0;
    const isTravellerClassFiltered = filterSearch.travellerClass.length > 0;
    const isDepartmentFiltered = filterSearch.department.length > 0;
    const isStatusFiltered = filterSearch.status.length > 0;

    if (
      !(
        isProductClassFiltered ||
        isTravellerClassFiltered ||
        isDepartmentFiltered ||
        isStatusFiltered
      )
    ) {
      return sortedItems;
    }

    return sortedItems.filter((ticket) => {
      const matchesProductClass =
        !isProductClassFiltered ||
        filterSearch.productClass?.some(
          (productClass) => productClass.id === ticket.product_class,
        );
      const ticketTravellerClassTags = getTravellerClassTags(ticket);
      const matchesTravellerClass =
        !isTravellerClassFiltered ||
        filterSearch.travellerClass?.some((travellerClass) =>
          ticketTravellerClassTags.includes(travellerClass.id),
        );
      const matchesDepartment =
        !isDepartmentFiltered ||
        filterSearch.department?.some(
          (department) => department.id === ticket.department_id,
        );
      const matchesStatus =
        !isStatusFiltered ||
        filterSearch.status?.some(
          (status) => status.id === displayedTicketState(ticket.state),
        );

      return (
        matchesProductClass &&
        matchesTravellerClass &&
        matchesDepartment &&
        matchesStatus
      );
    });
  }, [sortedItems, filterSearch]);

  const onDropdownChange = useCallback(
    (
      filter: OptionsType[],
      filterType: 'productClass' | 'travellerClass' | 'department' | 'status',
    ) => {
      setFilterSearch((prev) => ({
        ...prev!,
        [filterType]: filter as FilterType[],
      }));

      handleSearchParams({
        [filterType]: filter.map((f) => f.id),
      });
    },
    [handleSearchParams, setFilterSearch],
  );

  // Announce changes to sort and filter for screen readers
  useEffect(() => {
    if (!filterSearch) return;

    const count = filteredTickets.length;
    const sortLabel =
      filterSearch.sort === 'created_at'
        ? formatMessage({ defaultMessage: 'datum' })
        : formatMessage({ defaultMessage: 'status' });
    const directionLabel =
      filterSearch.direction === 'asc'
        ? formatMessage({ defaultMessage: 'stigande' })
        : formatMessage({ defaultMessage: 'fallande' });

    const message = formatMessage(
      {
        defaultMessage:
          '{count} {count, plural, one {biljett} other {biljetter}} sorterade efter {sortLabel} {directionLabel}',
      },
      { count, sortLabel, directionLabel },
    );

    setAnnouncement(message);
  }, [filterSearch, filteredTickets.length, formatMessage]);

  useEffect(() => {
    if (filterSearch) {
      return;
    }

    const productClassIds = filterProductClass || [];
    const travellerClassIds = filterTravellerClass || [];
    const departmentIds = filterDepartment || [];
    const statusIds = filterStatus || [];

    const productClasses = productClassIds
      .map((id) => optionsProductClass.find((d) => d.id === id))
      .filter(Boolean) as FilterType[];

    const travellerClasses = travellerClassIds
      .map((id) => optionsTravellerClass.find((d) => d.id === id))
      .filter(Boolean) as FilterType[];

    const departments = departmentIds
      .map((id) => optionsDepartments.find((d) => d.id === id))
      .filter(Boolean) as FilterType[];

    const statuses = statusIds
      .map((id) => optionsStatus.find((s) => s.id === id))
      .filter(Boolean) as FilterType[];

    setFilterSearch({
      query: query ?? '',
      sort: sort ?? 'created_at',
      direction: direction ?? 'desc',
      productClass: productClasses,
      travellerClass: travellerClasses,
      department: departments,
      status: statuses,
    });
  }, [
    direction,
    query,
    sort,
    filterSearch,
    setFilterSearch,
    filterProductClass,
    filterTravellerClass,
    filterDepartment,
    filterStatus,
    optionsProductClass,
    optionsTravellerClass,
    optionsDepartments,
    optionsStatus,
  ]);

  const renderTicketListItem = useCallback(
    (ticket: IssuedTicketFindResultDTO) => {
      return <TicketListItem key={ticket.id} ticket={ticket} />;
    },
    [],
  );

  const handleVisibleItemsChange = useVisibleTicketsUpdater({
    queryKey: getGetTicketsForUserOrganizationQueryKey(),
  });

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      <DocumentHead
        titleKey="page.tickets.title"
        descriptionKey="page.tickets.description"
      />
      <Header
        title={<FormattedMessage defaultMessage="Biljetter" />}
        actions={
          canBuyTickets && (
            <ActionButtons>
              <Button
                startIcon={<IconTicketPlus />}
                onClick={() =>
                  navigate({
                    to: '/$tenantId/buy-ticket',
                    params: { tenantId },
                  })
                }
                variant="filled"
              >
                <FormattedMessage defaultMessage="Köp biljett" />
              </Button>
            </ActionButtons>
          )
        }
      />
      <FilteredListContainer>
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          {announcement}
        </div>
        <RightPanel ref={rightPanelRef}>
          {filterSearch && (
            <FilterPanel
              firstInput={
                <Input
                  type="text"
                  label={formatMessage({ defaultMessage: 'Sök' })}
                  placeholder={formatMessage({
                    defaultMessage:
                      'Sök resenär, ID, Bobcat order-ID eller MTB',
                  })}
                  id="ticket-search"
                  value={filterSearch.query}
                  onChange={(e) => handleSearch(e.target.value)}
                />
              }
              filterOptions={[
                <Dropdown
                  key={optionsProductClass.length}
                  label={formatMessage({ defaultMessage: 'Produkt' })}
                  id="product-class"
                  options={optionsProductClass}
                  placeholder={formatMessage({ defaultMessage: 'Alla' })}
                  value={filterSearch.productClass}
                  onValueChange={(value) =>
                    onDropdownChange(value, 'productClass')
                  }
                  aria-description={formatMessage({
                    defaultMessage:
                      'Filtrera biljetter genom att välja produkt',
                  })}
                  multiSelect
                  containerRef={rightPanelRef}
                />,
                <Dropdown
                  key={optionsTravellerClass.length}
                  label={formatMessage({ defaultMessage: 'Resenärklass' })}
                  id="traveller-class"
                  options={optionsTravellerClass}
                  placeholder={formatMessage({ defaultMessage: 'Alla' })}
                  value={filterSearch.travellerClass}
                  onValueChange={(value) =>
                    onDropdownChange(value, 'travellerClass')
                  }
                  aria-description={formatMessage({
                    defaultMessage:
                      'Filtrera biljetter genom att välja resenärsklass',
                  })}
                  multiSelect
                  containerRef={rightPanelRef}
                />,
                <Dropdown
                  key={optionsDepartments.length}
                  label={formatMessage({ defaultMessage: 'Avdelning' })}
                  id="department"
                  options={optionsDepartments}
                  placeholder={formatMessage({ defaultMessage: 'Alla' })}
                  value={filterSearch.department}
                  onValueChange={(value) =>
                    onDropdownChange(value, 'department')
                  }
                  aria-description={formatMessage({
                    defaultMessage:
                      'Filtrera biljetter genom att välja avdelning',
                  })}
                  multiSelect
                  containerRef={rightPanelRef}
                />,
                <Dropdown
                  key={optionsStatus.length}
                  label={formatMessage({ defaultMessage: 'Status' })}
                  id="status"
                  options={optionsStatus}
                  placeholder={formatMessage({ defaultMessage: 'Alla' })}
                  value={filterSearch.status}
                  onValueChange={(value) => onDropdownChange(value, 'status')}
                  aria-description={formatMessage({
                    defaultMessage: 'Filtrera biljetter genom att välja status',
                  })}
                  multiSelect
                  containerRef={rightPanelRef}
                />,
              ]}
              sortingOptions={[
                <Button
                  key="created_at"
                  variant={
                    filterSearch.sort === 'created_at' ? 'filled' : 'outline'
                  }
                  startIcon={getIcon({
                    selectedSort: filterSearch.sort!,
                    wantedSort: 'created_at',
                    sortDirection: filterSearch.direction!,
                  })}
                  onClick={() => handleSort('created_at')}
                  aria-label={`Sortera efter köpdatum ${filterSearch.direction === 'asc' ? 'stigande' : 'fallande'}`}
                  aria-pressed={filterSearch.sort === 'created_at'}
                >
                  <FormattedMessage defaultMessage="Köpdatum" />
                </Button>,
              ]}
            />
          )}
        </RightPanel>
        <VirtualizedList
          count={filteredTickets.length}
          countName="biljetter"
          filteredItems={filteredTickets}
          totalItems={tickets as []}
          renderItem={renderTicketListItem}
          getItemKey={(ticket) => ticket.id}
          onVisibleItemsChange={handleVisibleItemsChange}
        />
      </FilteredListContainer>
    </>
  );
}
